import dayjs from 'dayjs'
import { v4 as uuid } from 'uuid'
import type { AppDispatch } from '../../../store'
import type { ChartCommand } from './chartCommands'
import type { ChartCommandApplyEntry } from '../../screener/aiChat/types'
import type { ChartCommandFailureReason } from './chartCommandMessages'
import Chart from '../chart'
import { ChartAlertSnapshot } from '../../alerts/alertModal.typings'
import {
    AlertExpression,
    AlertExpression_Type,
    Condition,
    Condition_CompositeCondition_Operator,
    Condition_SecurityAlertCondition,
    Condition_SecurityAlertCondition_Operator,
    UserAlert,
    UserAlert_Status,
    UserAlert_Trigger,
} from '../../../proto/generated/nexus'
import { exchangeFromJSON, NotificationChannel, TimeBucket_TimeSeries } from '../../../proto/generated/common'
import { Indicator } from '../../../proto/generated/indicator'
import {
    buildAlertPatternFromSeriesGroup,
    createUserAlertFromPattern,
    createUserAlertFromSnapshot,
    firstIndicatorOutputCodeForAlertUi,
    getCreateUserAlertRequestObject,
    getOperatorSource,
    getUserAlertMessageFromCondition,
} from '../../../util/alerts'
import { forestModelToCondition, leafNode } from '../../../util/alertConditionTree'
import { convertMapToKeyValueArray } from '../../../util/common'
import { createAlert, removeAlert, restartAlert, stopAlert } from '../../../services/apis/alerts'
import {
    addUserAlert,
    removeUserAlert,
    setAlertModalData,
    updateUserAlert,
} from '../../../reducer/alerts.reducer'
import { selectIndicators } from '../../../selectors/chart'
import { selectUserAlerts } from '../../../selectors/alerts'
import store from '../../../store'
import { findPatternIndicator } from '../../../util/patternIndicators'
import { FeatureCategory, FeatureSubCategory } from '../../adminUserRestrictions/Restrictions.constants'
import { FeatureCode } from '../../../util/RestrictionFeatureCode'
import { hasFeatureAccess } from '../../../util/accessControl'
import { selectAllFeatures, selectUserSubscriptionPlans, selectUserSubscriptionPlansData } from '../../../selectors/user'
import { setFeatureRestrictionDialog, Group } from '../../../reducer/user.reducer'
import { MAX_ACTIVE_ALERTS } from './chartAlertConstants'

export type AlertOperandCommand = {
    kind: 'price' | 'volume' | 'indicator' | 'compare' | 'pattern' | 'value'
    value?: number
    series_group_id?: string
    output?: string
    exchange?: string
    code?: string
    source?: string
}

type AlertConditionCommand = {
    operator: string
    left: AlertOperandCommand
    right: AlertOperandCommand
}

export type ChartAlertCommandDeps = {
    viewKey: string
    chart: Chart | undefined
    dispatch: AppDispatch
    getChartSnapshot?: () => ChartAlertSnapshot | null
}

const OPERATOR_MAP: Record<string, Condition_SecurityAlertCondition_Operator> = {
    crossing: Condition_SecurityAlertCondition_Operator.CROSSING,
    crossing_up: Condition_SecurityAlertCondition_Operator.CROSSING_UP,
    crossing_down: Condition_SecurityAlertCondition_Operator.CROSSING_DOWN,
    equal_to: Condition_SecurityAlertCondition_Operator.EQUAL_TO,
    greater_than: Condition_SecurityAlertCondition_Operator.GREATER_THAN,
    less_than: Condition_SecurityAlertCondition_Operator.LESS_THAN,
    appears: Condition_SecurityAlertCondition_Operator.APPEARS,
    entering: Condition_SecurityAlertCondition_Operator.ENTERING,
    exiting: Condition_SecurityAlertCondition_Operator.EXITING,
    inside: Condition_SecurityAlertCondition_Operator.INSIDE,
    outside: Condition_SecurityAlertCondition_Operator.OUTSIDE,
}

const TRIGGER_MAP: Record<string, UserAlert_Trigger> = {
    once: UserAlert_Trigger.ONCE,
    every_time: UserAlert_Trigger.EVERYTIME,
    once_per_bar: UserAlert_Trigger.ONCE_PER_BAR,
    once_per_bar_close: UserAlert_Trigger.ONCE_PER_BAR_CLOSED,
    once_per_minute: UserAlert_Trigger.ONCE_PER_MINUTE,
}

const CHANNEL_MAP: Record<string, NotificationChannel> = {
    desktop_popup: NotificationChannel.DESKTOP_POPUP,
    push_notification: NotificationChannel.PUSH_NOTIFICATION,
}

function userHasAlertAccess(): boolean {
    const state = store.getState()
    const plans = selectUserSubscriptionPlans(state) ?? []
    const plansData = selectUserSubscriptionPlansData(state)
    const allFeatures = selectAllFeatures(state)
    if (!plansData || !plans.length) return true
    const userFeatures = plans.flatMap((plan) => plansData[plan] ?? [])
    return hasFeatureAccess(
        allFeatures,
        userFeatures,
        FeatureCategory.CHART,
        FeatureSubCategory.ALERTS,
    )
}

function showAlertFeatureRestriction(dispatch: AppDispatch) {
    dispatch(
        setFeatureRestrictionDialog({
            open: true,
            feature: 'Alerts',
            group: Group.CHART,
            featureRestrictionCode: FeatureCode.CHART_ALERTS,
        }),
    )
}

function resolveOperator(raw: string): Condition_SecurityAlertCondition_Operator {
    const key = String(raw || 'crossing').toLowerCase()
    const operator = OPERATOR_MAP[key]
    if (operator === undefined) {
        throw new Error(`Unsupported alert operator "${key}".`)
    }
    return operator
}

function resolveTrigger(raw: string | undefined): UserAlert_Trigger {
    const key = String(raw || 'once').toLowerCase()
    const trigger = TRIGGER_MAP[key]
    if (trigger === undefined) {
        throw new Error(`Unsupported alert trigger "${key}".`)
    }
    return trigger
}

function resolveExpiryMs(command: ChartCommand): number {
    const preset = String(command.expiry || '1_month').toLowerCase()
    const now = dayjs()
    switch (preset) {
        case 'open_ended':
            return now.add(100, 'year').set('seconds', 0).set('milliseconds', 0).toDate().getTime()
        case 'end_of_day':
            return now.endOf('day').toDate().getTime()
        case '1_week':
            return now.add(1, 'week').set('seconds', 0).set('milliseconds', 0).toDate().getTime()
        case 'custom': {
            const dateText = String(command.expiry_date || command.date || '').trim()
            const parsed = dayjs(dateText)
            if (!parsed.isValid()) {
                throw new Error('Invalid expiry_date')
            }
            return parsed.endOf('day').toDate().getTime()
        }
        case '1_month':
            return now.add(1, 'month').set('seconds', 0).set('milliseconds', 0).toDate().getTime()
        default:
            throw new Error(`Unsupported alert expiry "${preset}".`)
    }
}

function resolveNotificationChannels(command: ChartCommand): NotificationChannel[] {
    const raw = command.notification_channels
    if (!Array.isArray(raw) || !raw.length) {
        return [NotificationChannel.DESKTOP_POPUP]
    }
    return raw.map((entry) => {
        const key = String(entry).toLowerCase()
        const channel = CHANNEL_MAP[key]
        if (channel === undefined) {
            throw new Error(`Unsupported notification channel "${key}".`)
        }
        return channel
    })
}

function basePriceExpression(
    snapshot: ChartAlertSnapshot,
    operator: Condition_SecurityAlertCondition_Operator,
): AlertExpression {
    if (snapshot.expressionMode) {
        return {
            type: AlertExpression_Type.SPREAD_SECURITY,
            spreadSecurity: {
                spreadExpression: snapshot.expression,
                security: snapshot.securityList.map((sec) => ({ exchange: sec.exchange, code: sec.code })),
                source: getOperatorSource(operator),
            },
            timeBucket: { timeSeries: TimeBucket_TimeSeries.SECOND, value: 1 },
        }
    }
    const primary = snapshot.securityList[0]
    return {
        type: AlertExpression_Type.SIMPLE_SECURITY,
        simpleSecurity: {
            security: { exchange: primary.exchange, code: primary.code },
            source: getOperatorSource(operator),
        },
        timeBucket: { timeSeries: TimeBucket_TimeSeries.SECOND, value: 1 },
    }
}

function buildOperandExpression(
    operand: AlertOperandCommand,
    snapshot: ChartAlertSnapshot,
    operator: Condition_SecurityAlertCondition_Operator,
    chart: Chart | undefined,
    indicators: Indicator[],
    side: 'left' | 'right',
): AlertExpression {
    const kind = operand.kind
    if (kind === 'value') {
        return {
            type: AlertExpression_Type.FIXED_VALUE,
            fixedValue: Number(operand.value ?? snapshot.data.close ?? 0),
            timeBucket: undefined,
        }
    }
    if (kind === 'price') {
        return basePriceExpression(snapshot, operator)
    }
    if (kind === 'volume') {
        const base = basePriceExpression(snapshot, operator)
        if (base.type === AlertExpression_Type.SPREAD_SECURITY && base.spreadSecurity) {
            return {
                ...base,
                spreadSecurity: { ...base.spreadSecurity, source: 'volume' },
            }
        }
        if (base.simpleSecurity) {
            return {
                ...base,
                simpleSecurity: { ...base.simpleSecurity, source: 'volume' },
            }
        }
        return base
    }
    if (kind === 'compare') {
        const exchange = exchangeFromJSON(String(operand.exchange))
        return {
            type: AlertExpression_Type.SIMPLE_SECURITY,
            simpleSecurity: {
                security: { exchange, code: String(operand.code) },
                source: String(operand.source || 'close'),
            },
            timeBucket: snapshot.timeBucket,
        }
    }
    if (kind === 'indicator') {
        const seriesGroupId = String(operand.series_group_id || '')
        const group = chart?.chartConfig.seriesGroups?.[seriesGroupId]
        if (!group) {
            throw new Error(`Indicator series group ${seriesGroupId} not found on chart`)
        }
        const indicatorDef = indicators.find((item) => item.code === group.scriptCode)
        if (!indicatorDef?.isAlertSupported) {
            throw new Error(`Indicator ${group.scriptCode} does not support alerts`)
        }
        const output =
            String(operand.output || '').trim() ||
            firstIndicatorOutputCodeForAlertUi(indicatorDef.outputs) ||
            Object.values(group.outputValues || {})[0]?.code ||
            ''
        if (!output) {
            throw new Error(`No alert output for indicator ${group.scriptCode}`)
        }
        const priceBase = basePriceExpression(snapshot, operator)
        return {
            type: AlertExpression_Type.TECHNICAL_INDICATOR,
            indicator: {
                indicatorCode: group.scriptCode,
                params: convertMapToKeyValueArray(group.inputValues || {}),
                output,
                expression: priceBase,
            },
            timeBucket: undefined,
        }
    }
    if (kind === 'pattern') {
        const seriesGroupId = String(operand.series_group_id || '')
        const group = chart?.chartConfig.seriesGroups?.[seriesGroupId]
        if (!group) {
            throw new Error(`Pattern series group ${seriesGroupId} not found on chart`)
        }
        const indicatorDef = findPatternIndicator(group.scriptCode, indicators)
        if (!indicatorDef) {
            throw new Error(`Pattern indicator ${group.scriptCode} not found`)
        }
        const primary = snapshot.securityList[0]
        const pattern = buildAlertPatternFromSeriesGroup(
            { id: group.id, scriptCode: group.scriptCode, inputValues: group.inputValues },
            primary,
            indicators,
        )
        return {
            type: AlertExpression_Type.PATTERN,
            pattern,
            timeBucket: undefined,
        }
    }
    throw new Error(`Unsupported alert operand kind on ${side}`)
}

function buildSecurityCondition(
    condition: AlertConditionCommand,
    snapshot: ChartAlertSnapshot,
    chart: Chart | undefined,
    indicators: Indicator[],
): Condition_SecurityAlertCondition {
    const operator = resolveOperator(condition.operator)
    if (operator === Condition_SecurityAlertCondition_Operator.APPEARS) {
        const left = buildOperandExpression(
            condition.left?.kind === 'pattern' ? condition.left : { kind: 'pattern', series_group_id: condition.left?.series_group_id },
            snapshot,
            operator,
            chart,
            indicators,
            'left',
        )
        return {
            operator,
            lhsExpression: left,
            rhsExpression: { type: AlertExpression_Type.FIXED_VALUE, fixedValue: 0, timeBucket: undefined },
        }
    }
    const left = buildOperandExpression(
        condition.left || { kind: 'price' },
        snapshot,
        operator,
        chart,
        indicators,
        'left',
    )
    const right = buildOperandExpression(
        condition.right || { kind: 'value', value: snapshot.data.close },
        snapshot,
        operator,
        chart,
        indicators,
        'right',
    )
    return {
        operator,
        lhsExpression: left,
        rhsExpression: right,
    }
}

function buildAlertCondition(
    command: ChartCommand,
    snapshot: ChartAlertSnapshot,
    chart: Chart | undefined,
    indicators: Indicator[],
): Condition {
    const composite = command.conditions
    if (Array.isArray(composite) && composite.length >= 2) {
        const logical = String(command.logical_operator || 'and').toLowerCase()
        const op =
            logical === 'or'
                ? Condition_CompositeCondition_Operator.OR
                : Condition_CompositeCondition_Operator.AND
        const model = {
            rootSiblingOp: op,
            roots: (composite as AlertConditionCommand[]).map((entry) =>
                leafNode(buildSecurityCondition(entry, snapshot, chart, indicators)),
            ),
        }
        const built = forestModelToCondition(model)
        if (!built) {
            throw new Error('Failed to build composite alert condition')
        }
        return built
    }
    const securityCondition = buildSecurityCondition(
        {
            operator: String(command.operator || 'crossing'),
            left: (command.left as AlertOperandCommand) || { kind: 'price' },
            right: (command.right as AlertOperandCommand) || { kind: 'value', value: snapshot.data.close },
        },
        snapshot,
        chart,
        indicators,
    )
    return { securityAlertCondition: securityCondition }
}

async function buildUserAlertFromCommand(
    command: ChartCommand,
    deps: ChartAlertCommandDeps,
): Promise<UserAlert> {
    const snapshot = deps.getChartSnapshot?.()
    if (!snapshot) {
        throw new Error('Chart snapshot unavailable')
    }
    const indicators = selectIndicators(store.getState())
    const operator = resolveOperator(String(command.operator || 'crossing'))
    let draft: UserAlert
    if (operator === Condition_SecurityAlertCondition_Operator.APPEARS) {
        const seriesGroupId = String((command.left as AlertOperandCommand)?.series_group_id || '')
        const group = deps.chart?.chartConfig.seriesGroups?.[seriesGroupId]
        if (!group) {
            throw new Error('Pattern indicator must be on chart for pattern alerts')
        }
        const primary = snapshot.securityList[0]
        const pattern = buildAlertPatternFromSeriesGroup(
            { id: group.id, scriptCode: group.scriptCode, inputValues: group.inputValues },
            primary,
            indicators,
        )
        draft = createUserAlertFromPattern(snapshot, pattern)
    } else {
        draft = createUserAlertFromSnapshot(snapshot)
        draft.condition = buildAlertCondition(command, snapshot, deps.chart, indicators)
    }
    draft.id = uuid()
    draft.trigger = resolveTrigger(String(command.trigger || 'once'))
    draft.expiry = resolveExpiryMs(command)
    draft.notificationChannels = resolveNotificationChannels(command)
    if (command.name) {
        draft.name = String(command.name)
    }
    const primary = snapshot.securityList[0]
    if (primary) {
        draft.security = { exchange: primary.exchange, code: primary.code }
    }
    const generatedMessage = await getUserAlertMessageFromCondition(
        draft.condition,
        primary?.symbol || primary?.code || '',
        indicators,
        { chartSnapshot: snapshot, chartTimeBucket: snapshot.timeBucket ?? draft.timeBucket },
    )
    draft.message = String(command.message || generatedMessage || draft.message || '')
    return draft
}

function activeAlertCount(): number {
    return selectUserAlerts(store.getState()).filter(
        (alert) => alert.status === UserAlert_Status.ACTIVE,
    ).length
}

function summarizeAlertsForList(): string {
    const alerts = selectUserAlerts(store.getState()).slice(0, 20)
    if (!alerts.length) return 'No alerts configured.'
    return alerts
        .map((alert) => {
            const status = UserAlert_Status[alert.status] || alert.status
            const label = alert.name || alert.message || alert.id
            return `${label} (${status})`
        })
        .join('; ')
}

export async function applyAlertCommand(
    command: ChartCommand,
    deps: ChartAlertCommandDeps,
): Promise<{ applied?: ChartCommandApplyEntry; failed?: ChartCommandApplyEntry; reason?: ChartCommandFailureReason }> {
    const op = String(command.op || '')

    if (op === 'list_alerts') {
        const count = activeAlertCount()
        return {
            applied: {
                op,
                message: `You have ${count} active alert(s) (${MAX_ACTIVE_ALERTS} max). ${summarizeAlertsForList()}`,
            },
        }
    }

    if (!userHasAlertAccess()) {
        showAlertFeatureRestriction(deps.dispatch)
        return { failed: { op, message: 'Alerts require a plan upgrade.' }, reason: 'feature_restricted' as ChartCommandFailureReason }
    }

    if (op === 'stop_alert' || op === 'restart_alert' || op === 'delete_alert') {
        const alertId = String(command.alert_id || command.id || '')
        try {
            if (op === 'stop_alert') {
                const updated = await stopAlert({ id: alertId })
                deps.dispatch(updateUserAlert(updated))
                return { applied: { op, message: `Stopped alert ${alertId}` } }
            }
            if (op === 'restart_alert') {
                if (activeAlertCount() >= MAX_ACTIVE_ALERTS) {
                    return {
                        failed: { op, message: `Cannot restart — ${MAX_ACTIVE_ALERTS} active alert limit reached.` },
                        reason: 'invalid_value',
                    }
                }
                const updated = await restartAlert({ id: alertId })
                deps.dispatch(updateUserAlert(updated))
                return { applied: { op, message: `Restarted alert ${alertId}` } }
            }
            await removeAlert({ id: alertId })
            deps.dispatch(removeUserAlert(alertId))
            return { applied: { op, message: `Deleted alert ${alertId}` } }
        } catch (error: any) {
            return {
                failed: { op, message: error?.message || `Failed to ${op.replace('_', ' ')}` },
                reason: 'not_found',
            }
        }
    }

    if (op !== 'create_alert') {
        return { failed: { op, message: 'Unsupported alert operation' }, reason: 'unsupported_op' }
    }

    if (activeAlertCount() >= MAX_ACTIVE_ALERTS) {
        return {
            failed: {
                op,
                message: `You already have ${MAX_ACTIVE_ALERTS} active alerts. Stop or delete one before creating another.`,
            },
            reason: 'invalid_value',
        }
    }

    try {
        const alert = await buildUserAlertFromCommand(command, deps)
        const snapshot = deps.getChartSnapshot?.()
        if (!snapshot) {
            return { failed: { op, message: 'Chart snapshot unavailable' }, reason: 'chart_unavailable' }
        }
        const mode = String(command.mode || 'modal').toLowerCase()
        if (mode === 'modal') {
            deps.dispatch(
                setAlertModalData({
                    chartSnapshot: snapshot,
                    alert,
                    edit: false,
                }),
            )
            return {
                applied: {
                    op,
                    message: alert.message || 'Opened alert dialog — review and click Create to confirm.',
                },
            }
        }
        const indicators = selectIndicators(store.getState())
        const created = await createAlert(
            getCreateUserAlertRequestObject(alert, {
                seriesGroups: deps.chart?.chartConfig.seriesGroups,
                indicators,
            }),
        )
        deps.dispatch(addUserAlert(created))
        return {
            applied: {
                op,
                message: created.message || created.name || 'Alert created.',
            },
        }
    } catch (error: any) {
        return {
            failed: { op, message: error?.message || 'Failed to create alert' },
            reason: 'invalid_value',
        }
    }
}

export function compactAlertsForContext(
    alerts: UserAlert[],
    symbolCodes: Set<string>,
): {
    active_count: number
    max_active: number
    items: Array<{ id: string; name?: string; message?: string; status?: string }>
} {
    const relevant = alerts.filter((alert) => {
        const code = alert.security?.code
        return !code || symbolCodes.has(code)
    })
    return {
        active_count: alerts.filter((a) => a.status === UserAlert_Status.ACTIVE).length,
        max_active: MAX_ACTIVE_ALERTS,
        items: relevant.slice(0, 10).map((alert) => ({
            id: alert.id,
            ...(alert.name ? { name: alert.name } : {}),
            ...(alert.message ? { message: alert.message.slice(0, 120) } : {}),
            status: UserAlert_Status[alert.status] || String(alert.status),
        })),
    }
}
